import { useState } from 'react'

import DatabaseMigration from '../components/DatabaseMigration'

import { Link, useNavigate, useParams } from 'react-router-dom'

import { canChangePlan } from '../lib/planChange'

import SectionNav from '../components/SectionNav'

import RequestError from '../components/RequestError'

import { engineName } from '../lib/navigation'

import { workspace } from '../lib/workspace'

import StatusBadge from '../components/StatusBadge'

import {

  Button,

  ConfirmDialog,

  CopyButton,

  Field,

  ListSkeleton,

  Skeleton,

} from '../components/ui'

import { api } from '../lib/api'

import { useLiveRefresh } from '../lib/events'

import PlanQuote from '../components/PlanQuote'

import { planPrice, relativeTime } from '../lib/format'

import { useToast } from '../lib/toast'

import type {

  DatabaseBackup,

  DatabaseCredentials,

  ManagedDatabase,

  PlanQuote as Quote,

} from '../lib/types'

import { useApi } from '../lib/useApi'



type Pending = { deleteData: boolean } | null



export default function DatabaseDetail() {

  const { databaseId = '' } = useParams()

  const navigate = useNavigate()

  const { attempt } = useToast()

  const [pending, setPending] = useState<Pending>(null)

  const [busy, setBusy] = useState(false)



  const { data: database, error, reload } = useApi(

    () => api.database(databaseId),

    [databaseId],

    60_000,

  )

  useLiveRefresh(reload, (event) => event.id === databaseId)



  if (error) return <div className="alert error">{error}</div>

  if (!database) return <DetailSkeleton />



  const confirmDelete = async () => {

    if (!pending) return

    setBusy(true)

    const ok = await attempt(

      () => api.deleteDatabase(database.id, pending.deleteData),

      {

        success: `Removal requested for ${database.name}`,

        failure: 'Could not remove the database',

      },

    )

    setBusy(false)

    setPending(null)

    if (ok) navigate('/databases')

  }



  return (

    <>

      <header className="page-head">

        <div className="titles">

          <div className="title-row">

            <h1>{database.name}</h1>

            <StatusBadge status={database.status} />

          </div>

          <p className="subtitle tiny">

            {engineName(database.engine)}{' '}

            {database.version}

            <span className="sep">·</span>

            {database.plan}

            <span className="sep">·</span>

            {database.disk_size_gb} GB

            <span className="sep">·</span>

            created {relativeTime(database.created_at)}

          </p>

        </div>

        <Link className="btn ghost" to="/databases">

          All databases

        </Link>

      </header>



      <SectionNav items={[{id:'connection',label:'Connection'},{id:'linked-components',label:'Components'},{id:'plan',label:'Plan'},{id:'backups',label:'Backups'},{id:'migration',label:'Migration'},{id:'danger',label:'Advanced'}]} />

      {database.status === 'creating' && (

        <div className="alert info">

          Provisioning storage and starting the database. Readiness will update automatically.

        </div>

      )}



      {database.status === 'failed' && (

        <div className="alert error">

          {database.error_message ?? 'This database failed to start.'}

          <p>Retry provisioning with the existing data and credentials.</p>

          <Button disabled={busy} onClick={async () => {

            setBusy(true)

            const ok = await attempt(() => api.retryDatabase(database.id), {

              success: 'Database provisioning queued',

              failure: 'Could not retry the database',

            })

            setBusy(false)

            if (ok) reload()

          }}>Retry provisioning</Button>

        </div>

      )}



      {database.status === 'suspended' && (

        <div className="alert warn">

          Suspended because the wallet ran out. The volume is intact — top up and

          it comes back with its data.

        </div>

      )}



      <section className="stack" id="connection">

        <h2 className="section-title">Connection</h2>

        <Connection database={database} />

      </section>



      <section className="stack" id="linked-components">

        <h2 className="section-title">Linked services</h2>

        <div className="list">

          {database.linked_services.length === 0 ? (

            <div className="list-item muted tiny">

              Not linked yet. Open a service, go to its Databases tab, and attach

              this one — the connection string is injected for you.

            </div>

          ) : (

            database.linked_services.map((name) => (

              <div className="list-item" key={name}>

                <span className="grow">{name}</span>

              </div>

            ))

          )}

        </div>

      </section>



      <section className="stack" id="plan">

        <h2 className="section-title">Plan</h2>

        {database.billing_project_id ? <div className="card card-pad-lg stack">

          <h2>Included in your application plan</h2>

          <p>CPU, memory and storage are shared with this application's components. One plan change updates them together, with no separate database plan charge.</p>

          <p>{database.disk_size_gb} GB requested storage · {database.status === 'available' ? 'Available' : 'Allocation not yet confirmed'}</p>

          <Link className="btn" to={`/projects/${database.billing_project_id}#plan`}>Manage application plan</Link>

        </div> : <PlanPicker key={`${database.id}:${database.plan}`} database={database} onChanged={reload} />}

      </section>



      {database.engine === 'postgres' && <DatabaseMigration database={database} />}

      <section className="stack" id="backups">

        <h2 className="section-title">Backups</h2>

        <Backups database={database} />

      </section>



      <section className="stack" id="danger">

        <h2 className="section-title">Danger zone</h2>

        <div className="card stack">

          <div className="row" style={{ justifyContent: 'space-between' }}>

            <div>

              <strong>Remove the instance, keep the data</strong>

              <p className="tiny muted">

                Stops billing. The volume stays, so a future database can be

                restored from it.

              </p>

            </div>

            <Button onClick={() => setPending({ deleteData: false })}>Remove</Button>

          </div>



          <div className="row" style={{ justifyContent: 'space-between' }}>

            <div>

              <strong>Destroy the instance and its data</strong>

              <p className="tiny muted">

                Permanently deletes the instance and storage volume. Existing backups follow their separate retention policy.

              </p>

            </div>

            <Button variant="danger" onClick={() => setPending({ deleteData: true })}>

              Destroy everything

            </Button>

          </div>

        </div>

      </section>



      <ConfirmDialog

        open={pending !== null}

        title={pending?.deleteData ? 'Destroy this database?' : 'Remove this database?'}

        destructive={pending?.deleteData}

        busy={busy}

        requireText={pending?.deleteData ? database.name : undefined}

        confirmLabel={pending?.deleteData ? 'Destroy everything' : 'Remove instance'}

        body={

          pending?.deleteData ? (

            <>

              This deletes <strong>{database.name}</strong> and its storage volume.

              Removal stays pending until storage cleanup finishes. Existing backups

              follow their separate retention policy.

            </>

          ) : (

            <>

              This stops <strong>{database.name}</strong> and ends its billing.

              The volume is kept, so the data survives.

            </>

          )

        }

        onConfirm={confirmDelete}

        onCancel={() => setPending(null)}

      />

    </>

  )

}



function Connection({ database }: { database: ManagedDatabase }) {

  const [credentials, setCredentials] = useState<DatabaseCredentials | null>(null)

  const [busy, setBusy] = useState(false)

  const { attempt } = useToast()



  const reveal = async () => {

    setBusy(true)

    await attempt(

      async () => setCredentials(await api.databaseCredentials(database.id)),

      { success: 'Credentials revealed — this was recorded in the audit log' },

    )

    setBusy(false)

  }



  return (

    <div className="card card-pad-lg stack">

      <Field

        label="Host"

        hint="Reachable only from your own services inside the cluster — never from the public internet."

      >

        <div className="row" style={{ gap: 8 }}>

          <code className="grow truncate">{database.internal_host ?? '—'}</code>

          {database.internal_host && (

            <CopyButton value={database.internal_host} label="Copy" />

          )}

        </div>

      </Field>



      <div className="field-row">

        <Field label="Port">

          <code className="tabular">{database.port}</code>

        </Field>

        <Field label="User">

          <code>{database.username}</code>

        </Field>

      </div>



      {credentials ? (

        <Field

          label="Connection string"

          htmlFor="conn"

          hint="Anyone with this string can read and write your data. Treat it like a password."

        >

          <div className="row" style={{ gap: 8 }}>

            <input

              id="conn"

              className="mono"

              readOnly

              value={credentials.connection_string}

              onFocus={(event) => event.currentTarget.select()}

            />

            <CopyButton value={credentials.connection_string} />

          </div>

        </Field>

      ) : (

        <div className="stack" style={{ gap: 6 }}>

          <Button

            loading={busy}

            disabled={database.status !== 'available'}

            onClick={reveal}

          >

            Reveal connection string

          </Button>

          <p className="hint">

            {database.status === 'available'

              ? 'Revealing credentials is recorded in the audit log.'

              : 'Available once the database is running.'}

          </p>

        </div>

      )}

    </div>

  )

}



function DetailSkeleton() {

  return (

    <>

      <header className="page-head">

        <div className="titles" style={{ gap: 10 }}>

          <Skeleton width={220} height={22} />

          <Skeleton width={330} height={12} />

        </div>

      </header>

      <div className="card card-pad-lg stack">

        <Skeleton width="35%" height={12} />

        <Skeleton height={38} />

        <Skeleton width="55%" height={12} />

      </div>

    </>

  )

}





/** Engines whose contents a logical dump captures. */

const BACKED_UP = new Set(['postgres', 'redis'])



/** Engines a dump can be loaded back into — mirrors RESTORABLE_ENGINES. */

const RESTORABLE = new Set(['postgres'])



function humanSize(bytes: number): string {

  if (bytes < 1024) return `${bytes} B`

  const units = ['KB', 'MB', 'GB']

  let value = bytes / 1024

  let unit = 0

  while (value >= 1024 && unit < units.length - 1) {

    value /= 1024

    unit += 1

  }

  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`

}



/**

 * Backups are listed failures and all: a gap in the history is the thing

 * worth seeing, and a list of successes alone cannot show one.

 */

function Backups({ database }: { database: ManagedDatabase }) {

  const { attempt } = useToast()

  const [busy, setBusy] = useState(false)

  const [restoring, setRestoring] = useState<DatabaseBackup | null>(null)

  const { data: backups, error, reload } = useApi(

    () => api.backups(database.id),

    [database.id],

  )



  if (!BACKED_UP.has(database.engine)) {

    return (

      <div className="card">

        <p className="tiny muted">

          Automatic backups cover PostgreSQL and Redis. This engine holds state

          a dump does not capture, so nothing is copied for it yet.

        </p>

      </div>

    )

  }



  const takeOne = async () => {

    setBusy(true)

    await attempt(() => api.createBackup(database.id), {

      success: 'Backup taken',

      failure: 'Could not take a backup',

    })

    setBusy(false)

    reload()

  }



  const confirmRestore = async () => {

    if (!restoring) return

    setBusy(true)

    await attempt(() => api.restoreBackup(database.id, restoring.id), {

      success: 'Restored — a copy of the previous contents is in the list',

      failure: 'Could not restore this backup',

    })

    setBusy(false)

    setRestoring(null)

    reload()

  }



  const save = (backup: DatabaseBackup) => {

    const stamp = backup.created_at.slice(0, 19).replace(/[:-]/g, '')

    return attempt(

      () =>

        api.downloadBackup(

          database.id,

          backup.id,

          `${database.slug}-${stamp}.dump`,

        ),

      { success: 'Downloading…', failure: 'Could not download this backup' },

    )

  }



  return (

    <div className="stack">

      <div className="card stack">

        <div className="row" style={{ justifyContent: 'space-between' }}>

          <div>

            <BackupPolicy databaseId={database.id} />

          </div>

          <Button

            loading={busy}

            onClick={takeOne}

            disabled={database.status !== 'available'}

          >

            Back up now

          </Button>

        </div>

      </div>



      {error && <div className="alert error">{error}</div>}



      {!backups && !error ? (

        <ListSkeleton rows={2} />

      ) : backups && backups.length === 0 ? (

        <div className="list">

          <div className="list-item muted tiny">

            No backups recorded yet. Take a backup now or review the schedule in Backups & recovery.

          </div>

        </div>

      ) : (

        <div className="list">

          {backups?.map((backup) => (

            <div className="list-item" key={backup.id}>

              <div className="grow">

                <div className="truncate">

                  {relativeTime(backup.created_at)}

                  {backup.trigger === 'manual' && (

                    <span className="tiny faint"> · taken by hand</span>

                  )}

                </div>

                {backup.status === 'failed' && (

                  <div className="tiny" style={{ color: 'var(--danger)' }}>

                    {backup.error_message ?? 'Failed'}

                  </div>

                )}

              </div>

              {backup.status === 'succeeded' ? (

                <>

                  <span className="mono tiny faint tabular">

                    {humanSize(backup.size_bytes)}

                  </span>

                  <Button variant="ghost" onClick={() => save(backup)}>

                    Download

                  </Button>

                  {RESTORABLE.has(database.engine) && (

                    <Button

                      variant="ghost"

                      onClick={() => setRestoring(backup)}

                      disabled={database.status !== 'available'}

                    >

                      Restore

                    </Button>

                  )}

                </>

              ) : (

                <span className="tiny faint">{backup.status}</span>

              )}

            </div>

          ))}

        </div>

      )}



      <ConfirmDialog

        open={restoring !== null}

        title="Restore this backup?"

        destructive

        busy={busy}

        requireText={database.name}

        confirmLabel="Restore"

        body={

          <>

            Everything currently in <strong>{database.name}</strong> is replaced

            with the contents of{' '}

            {restoring ? relativeTime(restoring.created_at) : ''}.

            <br />

            <br />

            A copy of what is there now is taken first, so this is reversible —

            it will appear in the list as a pre-restore backup.

          </>

        }

        onConfirm={confirmRestore}

        onCancel={() => setRestoring(null)}

      />

    </div>

  )

}





/** Move a database between plans, priced before it is agreed to.

 *

 * Separate from the service's plan on purpose. A customer whose API has

 * outgrown Starter has not necessarily outgrown Dev, and being made to move

 * both is how a platform turns one upgrade into two bills.

 *

 * Two things are said plainly rather than discovered: the disk does not move,

 * and the database restarts. Both are facts about the infrastructure — a

 * StatefulSet's volume claim cannot be resized in place, and changing a pod's

 * resources replaces it — and a customer who is not told will read the second

 * one as an outage.

 */

function PlanPicker({

  database,

  onChanged,

}: {

  database: ManagedDatabase

  onChanged: () => void

}) {

  const { data: plans, error: plansError, reload: retryPlans } = useApi(() => api.databasePlans(), [])

  const [quote, setQuote] = useState<Quote | null>(null)

  const [wanted, setWanted] = useState(database.plan)

  const [expandStorage, setExpandStorage] = useState(false)

  const [busy, setBusy] = useState(false)

  const { attempt } = useToast()



  const apply = async () => {

    if (!canChangePlan(quote, wanted, database.plan, true)) return

    setBusy(true)

    await attempt(

      async () => {

        await api.updateDatabase(database.id, wanted, expandStorage)

        onChanged()

      },

      {

        success: `${database.name} is moving to ${wanted}`,

        failure: 'Could not change the plan',

      },

    )

    setBusy(false)

  }



  return (

    <div className="card card-pad-lg stack">

      {plansError && <RequestError message={plansError} retry={retryPlans} />}

      <Field

        label="Size"

        htmlFor="db-plan"

        hint="CPU and RAM change with the plan. Existing storage is preserved; optional expansion grows the existing PVC in place. A restart may briefly interrupt connections."

      >

        <select

          id="db-plan"

          value={wanted}

          onChange={(event) => setWanted(event.target.value as ManagedDatabase['plan'])}

        >

          {(plans ?? []).map((plan) => (

            <option key={plan.tier} value={plan.tier}>

              {plan.label} — {plan.memory} RAM, {plan.storage_gb} GB ·{' '}

              {planPrice(plan)}/mo

            </option>

          ))}

        </select>

      </Field>



      {plans && plans.some(plan => plan.tier === wanted && plan.storage_gb > database.disk_size_gb) && (
        <label className="tiny muted">
          <input type="checkbox" checked={expandStorage} onChange={event => setExpandStorage(event.target.checked)} />
          Also expand this database disk to the selected plan's storage allowance. Optional; consumes Longhorn space and cannot be undone by shrinking.
        </label>
      )}

      <PlanQuote

        kind="database"

        id={database.id}

        plan={wanted}

        current={database.plan}

        onQuote={setQuote}

      />



      <div>

        <Button

          variant="primary"

          loading={busy}

          disabled={!plans || !!plansError || !canChangePlan(quote, wanted, database.plan, true) || database.status === 'creating'}

          onClick={apply}

        >

          {database.status === 'creating' ? 'Applying allocation…' : wanted === database.plan ? 'Apply current allocation' : 'Change plan'}

        </Button>

      </div>

    </div>

  )

}





function BackupPolicy({ databaseId }: { databaseId: string }) {

  const {data, error, reload} = useApi(() => workspace.protection(), [], 60_000)

  const protection = data?.find(item => item.id === databaseId)

  if (error) return <RequestError title="Backup schedule unavailable" message={error} retry={reload} />

  if (!data) return <p role="status">Checking backup schedule…</p>

  return <div className="stack" style={{gap: 6}}>

    <strong>{!protection ? 'No backup policy found' : protection.policy.enabled ? `Scheduled every ${protection.policy.interval_hours} hours` : 'Scheduled backups are off'}</strong>

    {protection && <><p className="tiny muted">Retention: {protection.policy.retention_days} days · Last successful backup: {protection.last_success_at ? relativeTime(protection.last_success_at) : 'None yet'}</p>

      {protection.next_due_at && <p className="tiny muted">Next scheduled: {new Date(protection.next_due_at).toLocaleString()}</p>}

      {protection.latest_error && <p className="alert error">{protection.latest_error}</p>}</>}

    <Link to="/protection">Manage schedule & restore copies →</Link>

  </div>

}

